/**
 * @fileoverview ClubOS Youth Academy — Coaches Management
 * License tracking, batch assignments, DOJ, document uploads.
 * All fields in single dialog — no tabs.
 * Roles and License levels sourced from AcademyConfig (localStorage: clubos_academy_config).
 * License Number, Expiry, Specialisation are display-only (disabled) — set by backend/admin.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import PropTypes from "prop-types";
import { academyService } from "api";
import FootballLoader from "components/FootballLoader";
import { TYPOGRAPHY, COLORS } from "config/branding";
import { buildBrandedReport, addWatermarkToPdf } from "config/brandingHelpers";

const LICENSE_COLOR = { A: "error", B: "warning", C: "info", D: "secondary", Grassroots: "success" };
const MAX_MB = 2;

const CONFIG_DEFAULTS = {
  coachRoles: ["Head Coach", "Assistant Coach", "GK Coach", "Fitness Coach", "Grassroots Coach", "Physio"],
  licenseLevels: ["A", "B", "C", "D", "Grassroots"],
};

const DEFAULT_LICENSE_LEVELS = [
  { id: "A", level: "A" },
  { id: "B", level: "B" },
  { id: "C", level: "C" },
  { id: "D", level: "D" },
  { id: "Grassroots", level: "Grassroots" },
];

// Role to License Level mapping
const ROLE_TO_LICENSE_LEVEL = {
  "Head Coach": "A",
  "Assistant Coach": "B",
  "GK Coach": "C",
  "Fitness Coach": "D",
  "Grassroots Coach": "Grassroots",
  "Physio": "D",
};

const SALUTATIONS = ["Mr.", "Ms.", "Mrs.", "Dr.", "Prof.", "Coach"];

const emptyForm = {
  salutation: "Mr.", fullName: "", role: "", licenseLevel: "",
  phone: "", email: "", notes: "", dateOfJoining: "", status: "Active",
};

// ── Validate ─────────────────────────────────────────────────────────────────
function validate(form) {
  const e = {};
  if (!form.fullName.trim())   e.fullName = "Full name is required.";
  if (!form.role)               e.role = "Role is required.";
  // License level is now auto-derived from role, so it's always set if role is set
  if (!form.phone.trim())       e.phone = "Phone is required.";
  else if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ""))) e.phone = "Enter a valid 10-digit number.";
  if (!form.email.trim())                                              e.email = "Email is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))           e.email = "Invalid email.";
  if (!form.dateOfJoining)      e.dateOfJoining = "Date of joining is required.";
  return e;
}

// ── File upload row ───────────────────────────────────────────────────────────
function FileRow({ label, accept, onFile, existingName }) {
  const ref = useRef(null);
  const [name, setName] = useState(existingName ?? "");
  const [err, setErr]   = useState("");

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { setErr(`Max ${MAX_MB} MB.`); e.target.value = ""; return; }
    setErr("");
    setName(f.name);
    onFile(f);
  };

  return (
    <SoftBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
      <SoftTypography variant="caption" color="text" sx={{ minWidth: 130 }}>{label}</SoftTypography>
      <SoftButton variant="outlined" color="primary" size="small" startIcon={<Icon>attach_file</Icon>}
        onClick={() => ref.current?.click()}>
        Browse
      </SoftButton>
      <input ref={ref} type="file" accept={accept} style={{ display: "none" }} onChange={pick} />
      {name
        ? <SoftBox display="flex" alignItems="center" gap={0.5}>
            <Icon sx={{ fontSize: "0.9rem", color: "success.main" }}>check_circle</Icon>
            <SoftTypography variant="caption" color="dark">{name}</SoftTypography>
          </SoftBox>
        : <SoftTypography variant="caption" color="text">No file · max {MAX_MB} MB</SoftTypography>
      }
      {err && <SoftTypography variant="caption" color="error">{err}</SoftTypography>}
    </SoftBox>
  );
}
FileRow.propTypes = {
  label: PropTypes.string.isRequired,
  accept: PropTypes.string,
  onFile: PropTypes.func.isRequired,
  existingName: PropTypes.string,
};

// ── Get avatar for coach based on role ──────────────────────────────────────
function getCoachAvatar(role) {
  const isPhysio = role && role.toLowerCase() === "physio";
  return isPhysio ? "/images/physio.svg" : "/images/coach.svg";
}

// ── Coach card ────────────────────────────────────────────────────────────────
function CoachCard({ coach, onEdit, assignedBatches, onDelete }) {
  return (
    <Card>
      <SoftBox p={2.5}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <SoftBox display="flex" alignItems="center" gap={1.5}>
            <SoftBox width={60} height={60} borderRadius="lg" display="flex" alignItems="center"
              justifyContent="center" sx={{ backgroundColor: "rgba(26,115,232,0.05)", flexShrink: 0, overflow: "hidden" }}>
              <img src={getCoachAvatar(coach.role)} alt={coach.role} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="h6" fontWeight="bold" color="dark" lineHeight={1.3}>
                {coach.salutation ? `${coach.salutation} ${coach.fullName}` : coach.fullName}
              </SoftTypography>
              <SoftTypography variant="caption" color="text">{coach.role || "—"}</SoftTypography>
            </SoftBox>
          </SoftBox>
          <SoftBox display="flex" gap={0.5}>
            <SoftButton variant="text" color="error" size="small" onClick={() => onDelete(coach)}>
              <Icon sx={{ fontSize: "1.1rem" }}>delete</Icon>
            </SoftButton>
            <SoftBadge badgeContent={coach.status} color={coach.status === "Active" ? "success" : "secondary"} size="xs" container />
          </SoftBox>
        </SoftBox>

        <SoftBox display="flex" alignItems="center" gap={1} mb={1} minHeight="24px">
          {coach.licenseLevel ? (
            <>
              <SoftBadge badgeContent={`License ${coach.licenseLevel}`} color={LICENSE_COLOR[coach.licenseLevel] ?? "info"} size="xs" container />
              {coach.licenseNumber && (
                <SoftTypography variant="caption" color="text" sx={{ fontFamily: "monospace" }}>
                  {coach.licenseNumber}
                </SoftTypography>
              )}
            </>
          ) : (
            <SoftTypography variant="caption" color="text">—</SoftTypography>
          )}
        </SoftBox>

        {coach.licenseExpiry && (
          <SoftBox display="flex" alignItems="center" gap={0.5} mb={1}>
            <Icon sx={{ fontSize: "0.85rem", color: "text.secondary" }}>event</Icon>
            <SoftTypography variant="caption" color="text">Expires: {String(coach.licenseExpiry).slice(0, 7)}</SoftTypography>
          </SoftBox>
        )}

        {coach.dateOfJoining && (
          <SoftBox display="flex" alignItems="center" gap={0.5} mb={1}>
            <Icon sx={{ fontSize: "0.85rem", color: "text.secondary" }}>calendar_today</Icon>
            <SoftTypography variant="caption" color="text">Joined: {String(coach.dateOfJoining).slice(0, 10)}</SoftTypography>
          </SoftBox>
        )}

        <SoftBox display="flex" gap={1} flexWrap="wrap" mb={1.5}>
          {coach.phone && (
            <SoftBox display="flex" alignItems="center" gap={0.5}>
              <Icon sx={{ fontSize: "0.85rem", color: "text.secondary" }}>phone</Icon>
              <SoftTypography variant="caption" color="text">{coach.phone}</SoftTypography>
            </SoftBox>
          )}
          {coach.email && (
            <SoftBox display="flex" alignItems="center" gap={0.5}>
              <Icon sx={{ fontSize: "0.85rem", color: "text.secondary" }}>email</Icon>
              <SoftTypography variant="caption" color="text">{coach.email}</SoftTypography>
            </SoftBox>
          )}
        </SoftBox>

        {/* ── Batch assignments ── */}
        <SoftBox display="flex" alignItems="flex-start" gap={1} mb={1.5} minHeight="24px">
          <SoftBox display="flex" alignItems="center" gap={0.5} flexShrink={0}>
            <Icon sx={{ fontSize: "0.8rem", color: "text.secondary" }}>category</Icon>
            <SoftTypography variant="caption" color="text" fontWeight="medium">
              Batches ({assignedBatches.length}):
            </SoftTypography>
          </SoftBox>
          {assignedBatches.length > 0 ? (
            <SoftBox display="flex" flexWrap="wrap" gap={0.5}>
              {assignedBatches.map((b) => (
                <Chip key={b.id} label={b.name} size="small"
                  sx={{ fontSize: "0.68rem", height: 20, backgroundColor: "rgba(26,115,232,0.08)", color: "#344767" }} />
              ))}
            </SoftBox>
          ) : (
            <SoftTypography variant="caption" color="text">—</SoftTypography>
          )}
        </SoftBox>

        {(coach.licenseDocumentUrl || coach.cvUrl) && (
          <SoftBox display="flex" gap={1} mb={1} flexWrap="wrap">
            {coach.licenseDocumentUrl && (
              <SoftButton variant="outlined" color="info" size="small"
                onClick={() => window.open(coach.licenseDocumentUrl, "_blank")}>
                <Icon sx={{ mr: 0.5, fontSize: "0.85rem" }}>description</Icon> License Doc
              </SoftButton>
            )}
            {coach.cvUrl && (
              <SoftButton variant="outlined" color="info" size="small"
                onClick={() => window.open(coach.cvUrl, "_blank")}>
                <Icon sx={{ mr: 0.5, fontSize: "0.85rem" }}>article</Icon> CV
              </SoftButton>
            )}
          </SoftBox>
        )}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center">
          <SoftTypography variant="caption" color="text">
            {assignedBatches.length > 0
              ? `${assignedBatches.length} ${assignedBatches.length === 1 ? "batch" : "batches"} assigned`
              : "No batches assigned"}
          </SoftTypography>
          <SoftButton variant="text" color="primary" size="small" onClick={() => onEdit(coach)}>
            <Icon sx={{ mr: 0.5 }}>edit</Icon> Edit
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}
CoachCard.propTypes = {
  coach: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  assignedBatches: PropTypes.array.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// ── Generate PDF report ───────────────────────────────────────────────────────
async function downloadCoachesPDF(coaches, batches) {
  const { doc, autoTable, startY: headerY, headStyles, altStyles } = await buildBrandedReport(
    "Academy Coaches Report",
    [`Total: ${coaches.length} coaches · Active: ${coaches.filter((c) => c.status === "Active").length}`],
  );
  const licenseLevelOrder = { A: 1, B: 2, C: 3, D: 4, Grassroots: 5 };
  const sorted = [...coaches].sort((a, b) =>
    (licenseLevelOrder[a.licenseLevel] ?? 6) - (licenseLevelOrder[b.licenseLevel] ?? 6)
  );
  autoTable(doc, {
    startY: headerY,
    head: [["#", "Name", "Role", "License", "License No.", "Expiry", "Batches Assigned", "Phone", "Email", "Joined", "Status"]],
    body: sorted.map((c, i) => {
      const assigned = batches.filter((b) => (b.coachIds ?? []).includes(c.id)).map((b) => b.name).join(", ");
      return [
        i + 1,
        `${c.salutation ?? ""} ${c.fullName}`.trim(),
        c.role ?? "",
        c.licenseLevel ?? "",
        c.licenseNumber ?? "",
        c.licenseExpiry ? String(c.licenseExpiry).slice(0, 10) : "",
        assigned || "—",
        c.phone ?? "",
        c.email ?? "",
        c.dateOfJoining ? String(c.dateOfJoining).slice(0, 10) : "",
        c.status ?? "",
      ];
    }),
    styles: { fontSize: 7.5, cellPadding: 2 },
    headStyles,
    alternateRowStyles: altStyles,
    columnStyles: { 6: { cellWidth: 40 } },
  });
  await addWatermarkToPdf(doc);
  doc.save(`coaches-report-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Main ──────────────────────────────────────────────────────────────────────
function AcademyCoaches() {
  const [coaches, setCoaches]           = useState([]);
  const [batches, setBatches]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [editing, setEditing]           = useState(null);
  const [form, setForm]                 = useState(emptyForm);
  const [saving, setSaving]             = useState(false);
  const [fieldErrors, setFieldErrors]   = useState({});
  const [saveError, setSaveError]       = useState("");
  const [licenseFile, setLicenseFile]   = useState(null);
  const [cvFile, setCvFile]             = useState(null);
  const [cfg, setCfg]                   = useState(CONFIG_DEFAULTS);
  const [licenseLevels, setLicenseLevels] = useState(DEFAULT_LICENSE_LEVELS);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [coachToDelete, setCoachToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const openDialog = () => {}; // cfg already loaded from API

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      academyService.getCoaches().catch(() => []),
      academyService.getBatches().catch(() => []),
      academyService.getCoachRoles().catch(() => null),
      academyService.getAgeGroups().catch(() => null),
      academyService.getLicenseLevels().catch(() => null),
    ]).then(([c, b, roles, _ag, levels]) => {
      setCoaches(Array.isArray(c) ? c : []);
      setBatches(Array.isArray(b) ? b : []);
      if (Array.isArray(roles) && roles.length) {
        setCfg((prev) => ({ ...prev, coachRoles: roles }));
      }
      if (Array.isArray(levels) && levels.length) {
        setLicenseLevels(levels);
      }
    }).finally(() => setLoading(false));
  }, []);

  // Helper: get batches a coach is assigned to
  const batchesForCoach = (coachId) =>
    batches.filter((b) => Array.isArray(b.coachIds) && b.coachIds.includes(coachId));

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    openDialog();
    setEditing(null); setForm(emptyForm); setFieldErrors({}); setSaveError("");
    setLicenseFile(null); setCvFile(null); setDialogOpen(true);
  };

  const openEdit = (coach) => {
    openDialog();
    setEditing(coach);
    const doj = coach.dateOfJoining ? String(coach.dateOfJoining) : "";
    setForm({
      salutation: coach.salutation ?? "Mr.", fullName: coach.fullName, role: coach.role,
      licenseLevel: coach.licenseLevel ?? "",
      phone: coach.phone ?? "", email: coach.email ?? "",
      notes: coach.notes ?? "",
      dateOfJoining: doj && !doj.startsWith("0001") ? doj.slice(0, 10) : "",
      status: coach.status ?? "Active",
    });
    setFieldErrors({}); setSaveError(""); setLicenseFile(null); setCvFile(null);
    setDialogOpen(true);
  };

  const f = (key) => (e) => {
    const newValue = e.target.value;
    setForm((p) => {
      const updated = { ...p, [key]: newValue };
      if (key === "role" && ROLE_TO_LICENSE_LEVEL[newValue]) {
        updated.licenseLevel = ROLE_TO_LICENSE_LEVEL[newValue];
      }
      return updated;
    });
    setFieldErrors((p) => ({ ...p, [key]: undefined }));
    setSaveError("");
  };

  const handleSave = async () => {
    const errs = validate(form);
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }
    setSaving(true); setSaveError("");
    try {
      const payload = { ...form, licenseLevel: form.licenseLevel || null, dateOfJoining: form.dateOfJoining || null, status: form.status || "Active" };
      if (editing) await academyService.updateCoach(editing.id, payload);
      else         await academyService.createCoach(payload);
      setDialogOpen(false); load();
    } catch (e) {
      setSaveError(e?.detail ?? e?.title ?? "Save failed.");
    } finally { setSaving(false); }
  };

  const handleDeleteClick = (coach) => {
    setCoachToDelete(coach);
    setDeleteError("");
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!coachToDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await academyService.deleteCoach(coachToDelete.id);
      setDeleteDialogOpen(false);
      setCoachToDelete(null);
      load();
    } catch (e) {
      setDeleteError(e?.detail ?? e?.title ?? e?.message ?? "Delete failed.");
    } finally { setDeleting(false); }
  };

  const active = coaches.filter((c) => c.status === "Active").length;

  // Helper: sort coaches by license level order (A > B > C > D > Grassroots)
  const licenseLevelOrder = { A: 1, B: 2, C: 3, D: 4, Grassroots: 5 };
  const sortedCoaches = [...coaches].sort((a, b) => {
    const orderA = licenseLevelOrder[a.licenseLevel] ?? 6;
    const orderB = licenseLevelOrder[b.licenseLevel] ?? 6;
    return orderA - orderB;
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" sx={{ color: TYPOGRAPHY.page.titleColor }}>Academy Coaches</SoftTypography>
            <SoftTypography variant="body2" sx={{ color: TYPOGRAPHY.page.subtitleColor }}>Coaching staff · license · batch assignments</SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1}>
            <SoftButton variant="outlined" color="primary" startIcon={<Icon>download</Icon>}
              onClick={() => downloadCoachesPDF(coaches, batches)}>
              Download Report
            </SoftButton>
            <SoftButton variant="gradient" color="primary" startIcon={<Icon>person_add</Icon>} onClick={openAdd}>
              Add Coach
            </SoftButton>
          </SoftBox>
        </SoftBox>

        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={6}>
              <MiniStatisticsCard title={{ text: "Total Coaches" }} count={String(coaches.length)}
                percentage={{ color: "primary", text: "on staff" }} icon={{ color: "primary", component: "sports_soccer" }} />
            </Grid>
            <Grid item xs={6} sm={6}>
              <MiniStatisticsCard title={{ text: "Active" }} count={String(active)}
                percentage={{ color: "success", text: "coaching" }} icon={{ color: "success", component: "check_circle" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {loading ? (
          <FootballLoader label="Loading coaches…" />
        ) : (
          <Grid container spacing={3}>
            {sortedCoaches.map((c) => (
              <Grid key={c.id} item xs={12} sm={6} lg={4}>
                <CoachCard coach={c} onEdit={openEdit} onDelete={handleDeleteClick} assignedBatches={batchesForCoach(c.id)} />
              </Grid>
            ))}
            {sortedCoaches.length === 0 && (
              <Grid item xs={12}>
                <SoftBox p={4} textAlign="center"><SoftTypography variant="caption" color="text">No coaches yet</SoftTypography></SoftBox>
              </Grid>
            )}
          </Grid>
        )}
      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Coach</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{deleteError}</Alert>}
          <SoftTypography variant="body2" sx={{ mt: deleteError ? 0 : 2 }}>
            Are you sure you want to delete <strong>{coachToDelete?.fullName}</strong>? This action cannot be undone.
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => { setDeleteDialogOpen(false); setDeleteError(""); }} disabled={deleting}>
            Cancel
          </SoftButton>
          <SoftButton variant="gradient" color="error" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Single-page dialog — no tabs ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { maxHeight: "90vh" } }}>
        <DialogTitle>{editing ? "Edit Coach" : "Add Coach"}</DialogTitle>
        <DialogContent dividers>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

          <Grid container spacing={2}>
            {/* Row 1: Salutation + Name */}
            <Grid item xs={4}>
              <TextField select fullWidth label="Salutation" value={form.salutation}
                onChange={f("salutation")} size="small"
                helperText=" ">
                {SALUTATIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={8}>
              <TextField fullWidth required label="Full Name" value={form.fullName}
                onChange={f("fullName")} size="small"
                error={Boolean(fieldErrors.fullName)}
                helperText={fieldErrors.fullName || " "} />
            </Grid>

            {/* Row 2: Role + Date of Joining */}
            <Grid item xs={6}>
              <TextField select fullWidth required label="Role" value={form.role}
                onChange={f("role")} size="small"
                error={Boolean(fieldErrors.role)}
                helperText={fieldErrors.role || " "}>
                <MenuItem value="" disabled><em>Select role…</em></MenuItem>
                {cfg.coachRoles.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="Date of Joining" type="date"
                value={form.dateOfJoining} onChange={f("dateOfJoining")} size="small"
                InputLabelProps={{ shrink: true }}
                error={Boolean(fieldErrors.dateOfJoining)}
                helperText={fieldErrors.dateOfJoining || " "} />
            </Grid>

            {/* Row 3: Phone + Email (both required) */}
            <Grid item xs={6}>
              <TextField fullWidth required label="Phone" value={form.phone}
                onChange={f("phone")} size="small"
                error={Boolean(fieldErrors.phone)}
                helperText={fieldErrors.phone || " "} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="Email" value={form.email}
                onChange={f("email")} size="small"
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email || " "} />
            </Grid>

            {/* Row 4: License Level + Status toggle */}
            <Grid item xs={6}>
              <TextField select fullWidth required label="License Level" value={form.licenseLevel}
                onChange={f("licenseLevel")} size="small"
                error={Boolean(fieldErrors.licenseLevel)}
                helperText={fieldErrors.licenseLevel || " "}>
                <MenuItem value="" disabled><em>Select level…</em></MenuItem>
                {licenseLevels.map((l) => (
                  <MenuItem key={l.id ?? l.level} value={l.level}>{l.label ?? l.level}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Status" value={form.status}
                onChange={f("status")} size="small"
                SelectProps={{
                  renderValue: (val) => (
                    <span style={{ color: val === "Active" ? "#4CAF50" : "#f44336", fontWeight: 600 }}>{val}</span>
                  ),
                }}>
                <MenuItem value="Active" sx={{ color: "#4CAF50", fontWeight: 600 }}>Active</MenuItem>
                <MenuItem value="Inactive" sx={{ color: "#f44336", fontWeight: 600 }}>Inactive</MenuItem>
              </TextField>
            </Grid>

            {/* Row 5: Notes */}
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" value={form.notes}
                onChange={f("notes")} size="small" multiline rows={2} />
            </Grid>

            {/* Documents */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1, mb: 0.5 }} />
              <SoftTypography variant="caption" color="text" fontWeight="medium">
                DOCUMENTS · max {MAX_MB} MB each
              </SoftTypography>
            </Grid>
            <Grid item xs={12}>
              <FileRow label="License Document" accept=".pdf,.jpg,.jpeg,.png"
                onFile={setLicenseFile}
                existingName={editing?.licenseDocumentUrl ? "Existing file" : ""} />
            </Grid>
            <Grid item xs={12}>
              <FileRow label="CV / Resume" accept=".pdf,.doc,.docx"
                onFile={setCvFile}
                existingName={editing?.cvUrl ? "Existing file" : ""} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editing ? "Update" : "Add Coach"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default AcademyCoaches;
